import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Belisanne: on first load with a preselected variant (?variant=), show that variant's
    // image on the mobile carousel without reordering the gallery. Desktop grid is left alone
    // so the page does not jump on load.
    if (!this.#isDesktop() && new URL(window.location.href).searchParams.has('variant')) {
      // Children may not be parsed yet when this runs during page load; defer.
      window.setTimeout(() => this.scrollToSelectedMedia({ animate: false }), 300);
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);

        // Belisanne: the gallery keeps its original order; bring the selected variant's image into view.
        if (newMediaGallery instanceof MediaGallery) {
          requestAnimationFrame(() => newMediaGallery.scrollToSelectedMedia({ animate: true }));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Belisanne: scrolls (desktop grid) or slides (mobile carousel) to the media
   * that belongs to the selected variant, identified by data-selected-media-id.
   * @param {{ animate?: boolean }} [options]
   */
  scrollToSelectedMedia({ animate = true } = {}) {
    const id = this.dataset.selectedMediaId;
    if (!id) return;

    const grid = this.querySelector('.media-gallery__grid');
    const item = grid?.querySelector(`[data-media-id="${id}"]`);
    if (!grid || !item) return;

    const index = Array.from(grid.children).indexOf(item);
    if (index < 0) return;

    if (this.#isDesktop() && this.presentation === 'grid') {
      item.scrollIntoView({ behavior: animate ? 'smooth' : 'auto', block: 'start' });
    } else {
      // The slideshow runs its own "select first slide" one frame after it connects,
      // which would undo an immediate select. Wait for that to settle, then move.
      this.#selectSlideWhenReady(index, animate);
    }
  }

  /**
   * @param {number} index
   * @param {boolean} animate
   * @param {number} [attempt]
   */
  #selectSlideWhenReady(index, animate, attempt = 0) {
    window.setTimeout(() => {
      const slideshow = this.slideshow;
      if (!this.isConnected) return;
      if (!slideshow || !slideshow.slides?.length) {
        if (attempt < 5) this.#selectSlideWhenReady(index, animate, attempt + 1);
        return;
      }
      slideshow.select(index, undefined, { animate });
    }, attempt === 0 ? 250 : 200);
  }

  #isDesktop() {
    return window.matchMedia('(min-width: 750px)').matches;
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
